import {
  LivestockEventCreateT,
  type LivestockMovementCreateT,
  type LivestockEventT,
  type LivestockEventUpdateT,
} from '@ruminati/types_v2/livestock_events'
import { type LivestockMobT } from '@ruminati/types_v2/livestock_mobs'
import {
  type LivestockOnboardingT,
  type LivestockOnboardingResponseT,
  type LivestockCohortCreateWithEventDetailsT,
  type LivestockOnboardingCohortResponseT,
} from '@ruminati/types_v2/livestock_onboarding'
import { LivestockSystemT, LivestockSystemUpdateT } from '@ruminati/types_v2/livestock_system'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { CommonApiV2Instance } from '@/services/common'

import { useMobsStore } from './mobs'
import { useOrganisationStore } from './organisations'
import { usePropertiesStore } from './properties'

export const useLivestockStore = defineStore('livestock', () => {
  const organisationStore = useOrganisationStore()
  const propertiesStore = usePropertiesStore()

  const showingDemoData = ref(true)

  const events = ref<Array<LivestockEventT> | undefined>()
  const livestockSystemDetails = ref<Array<LivestockSystemT> | undefined>()

  function setEvents(newEvents: Array<LivestockEventT>) {
    events.value = newEvents
  }

  function setSystemDetails(newDetails: Array<LivestockSystemT>) {
    livestockSystemDetails.value = newDetails
  }

  function updateSystemDetailsRecord(updated: LivestockSystemT) {
    const existing = livestockSystemDetails.value ?? []
    const index = existing.findIndex((d) => d.id === updated.id)
    if (index > -1) existing.splice(index, 1, updated)
    else existing.push(updated)
    livestockSystemDetails.value = existing
  }

  function resetAll() {
    events.value = undefined
    livestockSystemDetails.value = undefined
    showingDemoData.value = true
  }

  async function updateSystemDetails(recordId: string, body: LivestockSystemUpdateT) {
    const data = await CommonApiV2Instance.patch(
      `organisations/${organisationStore.currentOrg?.id}/livestock-system-details/${recordId}`,
      { json: body },
    ).json<LivestockSystemT>()
    if (data) {
      const idx = (livestockSystemDetails.value ?? []).findIndex((c) => c.id === recordId)
      if (idx !== -1) {
        livestockSystemDetails.value?.splice(idx, 1, data)
      }
    }
  }

  // For events that arrive as a byproduct of a different endpoint's response rather than through
  // createEvent's own POST /livestock-events call.
  function addEvent(event: LivestockEventT): void {
    if (!events.value) events.value = []
    events.value.push(event)
  }

  // For an edited event that arrives as a byproduct of a purpose-built endpoint's response (e.g.
  // a location-transfer update), rather than through updateEvent's own PATCH call.
  function replaceEvent(event: LivestockEventT): void {
    if (!events.value) events.value = []
    const idx = events.value.findIndex((e) => e.id === event.id)
    if (idx > -1) events.value.splice(idx, 1, event)
    else events.value.push(event)
  }

  // For an event deleted as a byproduct of a purpose-built endpoint (e.g. a location-transfer
  // delete), rather than through deleteEvent's own DELETE call.
  function removeEvent(eventId: string): void {
    if (!events.value) return
    const idx = events.value.findIndex((e) => e.id === eventId)
    if (idx > -1) events.value.splice(idx, 1)
  }

  async function syncMobsForEvent(event: LivestockEventT): Promise<void> {
    const mobIds = [...new Set(event.details.map((detail) => detail.mobId))]
    if (mobIds.length === 0) return
    const mobsStore = useMobsStore()
    await Promise.all(
      mobIds.map(async (mobId) => {
        try {
          const mob = await CommonApiV2Instance.get(
            `properties/${event.propertyId}/livestock-mobs/${mobId}`,
          ).json<LivestockMobT>()
          mobsStore.addOrReplace(event.propertyId, mob)
        } catch {
          // The event is already committed. A later property-mob query repairs a transient cache
          // refresh failure without making the successful mutation look failed.
        }
      }),
    )
  }

  async function createEvent(body: LivestockEventCreateT): Promise<LivestockEventT | undefined> {
    const propertyId = body.propertyId ?? propertiesStore.properties?.[0]?.id
    if (!propertyId) return
    const { propertyId: _propertyId, ...eventBody } = body
    const data = await CommonApiV2Instance.post(`properties/${propertyId}/livestock-events`, {
      json: eventBody,
    }).json<LivestockEventT>()
    if (data) {
      if (!events.value) events.value = []
      events.value.push(data)
      await syncMobsForEvent(data)
    }
    return data
  }

  async function createLivestockMovement(
    propertyId: string,
    body: LivestockMovementCreateT,
  ): Promise<LivestockEventT> {
    const data = await CommonApiV2Instance.post(`properties/${propertyId}/livestock-movements`, {
      json: body,
    }).json<LivestockEventT>()
    if (!events.value) events.value = []
    events.value.push(data)
    await syncMobsForEvent(data)
    return data
  }

  async function updateEvent(data: {
    propertyId: string
    eventId: string
    body: LivestockEventUpdateT
  }): Promise<LivestockEventT> {
    const existingEvent = events.value?.find((event) => event.id === data.eventId)
    const responseData = await CommonApiV2Instance.patch(
      `properties/${data.propertyId}/livestock-events/${data.eventId}`,
      { json: data.body },
    ).json<LivestockEventT>()
    if (events.value) {
      const idx = events.value.findIndex((e) => e.id === data.eventId)
      if (idx > -1) events.value.splice(idx, 1, responseData)
    }
    await syncMobsForEvent({
      ...responseData,
      details: [...(existingEvent?.details ?? []), ...responseData.details],
    })
    return responseData
  }

  async function deleteEvent(data: { propertyId: string; eventId: string }): Promise<void> {
    const existingEvent = events.value?.find((event) => event.id === data.eventId)
    const response = await CommonApiV2Instance.delete(
      `properties/${data.propertyId}/livestock-events/${data.eventId}`,
      { headers: { 'content-type': undefined } },
    )
    if (response.status === 204 && events.value) {
      const idx = events.value.findIndex((e) => e.id === data.eventId)
      if (idx > -1) events.value.splice(idx, 1)
    }
    if (existingEvent) await syncMobsForEvent(existingEvent)
  }

  // Onboarding creates authoritative mob rows and returns those rows alongside any descriptive
  // history events. Update the mob store here so the current-state UI does not wait for a refetch.
  async function submitOnboardingData(propertyId: string, data: LivestockOnboardingT) {
    const responseData = await CommonApiV2Instance.post(
      `properties/${propertyId}/livestock-onboarding`,
      { json: data },
    ).json<LivestockOnboardingResponseT>()
    if (!events.value) events.value = []
    events.value.push(...responseData.events)
    const mobsStore = useMobsStore()
    for (const mob of responseData.cohorts) mobsStore.addOrReplace(propertyId, mob)
    if (!livestockSystemDetails.value) livestockSystemDetails.value = []
    const systemIndex = livestockSystemDetails.value.findIndex(
      (system) => system.propertyId === responseData.systemDetails.propertyId,
    )
    if (systemIndex === -1) {
      livestockSystemDetails.value.push(responseData.systemDetails)
    } else {
      livestockSystemDetails.value.splice(systemIndex, 1, responseData.systemDetails)
    }
  }

  async function submitOnboardingDataSingleCohort(
    propertyId: string,
    data: LivestockCohortCreateWithEventDetailsT,
  ) {
    const responseData = await CommonApiV2Instance.post(
      `properties/${propertyId}/livestock-onboarding-cohort`,
      { json: data },
    ).json<LivestockOnboardingCohortResponseT>()
    if (!events.value) events.value = []
    events.value.push(...responseData.events)
    useMobsStore().addOrReplace(propertyId, responseData.cohort)
  }

  function toggleDemoMode(enabled: boolean) {
    showingDemoData.value = enabled
  }

  function completeOnboarding(payload: { events: LivestockEventT[] }) {
    events.value = payload.events
    showingDemoData.value = true
  }

  return {
    events,
    livestockSystemDetails,
    showingDemoData,

    setEvents,
    setSystemDetails,
    updateSystemDetailsRecord,
    resetAll,

    toggleDemoMode,
    completeOnboarding,

    addEvent,
    replaceEvent,
    removeEvent,
    createEvent,
    createLivestockMovement,
    updateEvent,
    deleteEvent,

    updateSystemDetails,
    submitOnboardingData,
    submitOnboardingDataSingleCohort,
  }
})

export default useLivestockStore
